import { useMemo } from 'react';
import { Form } from 'react-final-form';

import GlobalLoader from 'components/GlobalLoader';
import NavigatorContent from 'components/NavigatorContent';
import { InitialValues } from 'forms/types';
import useNavigatorTracking from 'hooks/useNavigatorTracking';
import Steps from 'hooks/useNavigatorTracking/steps';
import goToNextHealthStep from 'utils/goToNextHealthStep';

import { HEADING, SUBHEADING, TITLE } from './constants';
import HealthBasicsForm from './form';
import { HealthBasicsFormValues } from './form/types';
import { useHealthBasicsQuery } from './graphql/HealthBasics';
import { useUpdateHealthBasicsMutation } from './graphql/UpdateHealthBasics';
import buildInitialValues from './utils/buildInitialValues';
import buildInput from './utils/buildInput';
import { getValidator } from './utils/validate';

const HealthBasics = () => {
  const trackStepCompleted = useNavigatorTracking(Steps.HealthBasics);
  const { data, loading } = useHealthBasicsQuery();
  const initialValues = useMemo(() => buildInitialValues(data), [data]);
  const gender = data?.currentUser.gender;
  const validate = useMemo(() => (gender ? getValidator({ gender }) : undefined), [gender]);

  const [updateHealthBasics] = useUpdateHealthBasicsMutation({
    onCompleted(res) {
      trackStepCompleted();
      goToNextHealthStep(res.updateHealthBasics?.nextStepPath);
    },
  });

  const onSubmit = async (values: HealthBasicsFormValues) => {
    await updateHealthBasics({
      variables: {
        input: buildInput(values, gender),
      },
    });
  };

  if (loading) {
    return <GlobalLoader />;
  }

  return (
    <NavigatorContent heading={HEADING} subHeading={SUBHEADING} title={TITLE}>
      <Form<HealthBasicsFormValues, InitialValues<HealthBasicsFormValues>>
        initialValues={initialValues}
        onSubmit={onSubmit}
        validate={validate}
      >
        {(formProps) => <HealthBasicsForm {...formProps} gender={gender} />}
      </Form>
    </NavigatorContent>
  );
};

export default HealthBasics;
